import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    inject,
    OnDestroy,
    OnInit,
    Signal,
} from "@angular/core";

import { toSignal } from "@angular/core/rxjs-interop";
import { Subscription } from "rxjs";

import { LayoutFacade } from "@hermes/aphrodite/layout";
import {
    ENABLE_HORIZONTAL_MENU_HEADER,
    FeatureFlagFacade,
} from "@hermes/states/flipper";

import {
    HEADER_DESKTOP_HEIGHT,
    HEADER_DESKTOP_HEIGHT_WITH_MENU,
    HEADER_DESKTOP_HEIGHT_WITH_SKIP_LINKS,
    HEADER_MOBILE_HEIGHT,
} from "../../constants/header.constant";

/**
 * This component is used to positioned a top level component to be under the header
 * it takes the height of the header to apply a negative margin-top
 *
 * This is a component and not a directive because it needs to be accompanied with css that needs to be
 * load with the html serve on SSR and not in the main.css
 *
 * It manages accessibility attributes for you.
 *
 * Usage:
 * ```
 * <h-positioned-under-header>
 *      <h-your-component-you-want-under-header>
 *      </h-your-component-you-want-under-header>
 * </h-positioned-under-header>
 * ```
 *
 */
@Component({
    selector: "h-positioned-under-header",
    templateUrl: "./positioned-under-header.component.html",
    styleUrl: "./positioned-under-header.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
})
export class PositionedUnderHeaderComponent implements OnInit, OnDestroy {
    @HostBinding("style.margin-top")
    public marginTop: string | undefined = undefined;

    public readonly HEADER_MOBILE_HEIGHT = HEADER_MOBILE_HEIGHT;
    public readonly HEADER_DESKTOP_HEIGHT = HEADER_DESKTOP_HEIGHT;
    public readonly HEADER_DESKTOP_HEIGHT_WITH_MENU =
        HEADER_DESKTOP_HEIGHT_WITH_MENU;
    public readonly HEADER_DESKTOP_HEIGHT_WITH_SKIP_LINKS =
        HEADER_DESKTOP_HEIGHT_WITH_SKIP_LINKS;

    public isMenuHeaderFFActivated: Signal<boolean>;

    private subscription = new Subscription();

    private readonly layoutFacade = inject(LayoutFacade);
    private readonly featureFlagFacade = inject(FeatureFlagFacade);

    constructor() {
        this.isMenuHeaderFFActivated = toSignal(
            this.featureFlagFacade.isActivated(ENABLE_HORIZONTAL_MENU_HEADER),
            { initialValue: false },
        );
    }

    public ngOnInit() {
        this.layoutFacade.setHeaderTransparency(true);
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.layoutFacade.setHeaderTransparency(false);
    }
}
